// What the picker (src/client, runs on a project's page), the drawer (src/client/drawer.ts, on Snag's own
// origin) and the Worker (src/worker) agree on: limits, labels, the shape of a report and the window messages.

/** `claude`: the factory works it unattended and ships a fix. `owner-task`: a manual task for the owner. */
export const LABELS = ["claude", "owner-task"] as const;
export type Label = (typeof LABELS)[number];

/**
 * Caps. Title and description are typed by hand: over the cap is refused. What the picker captures is clipped (again
 * by the Worker), so a long page can never push the body past GitHub's 65,536 characters or bury the description.
 */
export const LIMITS = {
  title: 120,
  description: 6000,
  url: 500,
  route: 120,
  element: 160,
  selector: 400,
  heading: 120,
  headings: 6,
  text: 600,
  build: 40,
  /** the screenshot as uploaded (WebP, or PNG/JPEG where the browser can't encode WebP) */
  screenshotBytes: 1_500_000,
} as const;

/** Screenshots are served this long; the R2 bucket's lifecycle rule deletes them at the same age. */
export const SHOT_TTL_DAYS = 30;

/** A project is its repo's name under the GitHub owner (`GITHUB_OWNER`): `data-project="rankhint"`. */
export const PROJECT = /^[A-Za-z0-9][A-Za-z0-9._-]{0,99}$/;

/** Where the issue was reported from, captured by the picker. All of it is page data, never instructions. */
export interface IssueContext {
  /** the page, e.g. https://app.rankhint.com/sites/site_x?tab=audit */
  url: string;
  /** the page's path, or what the page says its route is (`data-route` / `snag.route`) */
  route: string;
  /** e.g. 1440×900 @2x, dark */
  viewport: string;
  /** a short description of the picked element, e.g. <h2> "Broken links", or the area dragged out over one */
  element: string;
  /** a CSS path to it from the nearest stable ancestor */
  selector: string;
  /** the headings it sits under, outermost first (page title › card title) */
  headings: string[];
  /** the start of its visible text */
  text: string;
}

/** POST /v1/report */
export interface ReportRequest {
  /** the drawer's id for this report: a retried or double-clicked submit answers with the issue already filed */
  report_id: string;
  project: string;
  label: Label;
  title: string;
  description: string;
  /** the project's build or version, if its page says (`data-build`) */
  build: string;
  /** where it was picked; null for an issue with nothing attached */
  context: IssueContext | null;
  /** base64, no data: prefix */
  screenshot: string | null;
}

export interface ReportResult {
  /** the issue's number; 0 for a report sent to a project's own address (HOOKS) */
  number: number;
  url: string;
  /** a project's own address: what it said back ("Added to design 1: the designer starts in a minute") */
  message?: string;
  /** Snag's own dev server: nothing was filed; `body` is what would have been */
  dry_run?: boolean;
  body?: string;
}

/**
 * The page and the drawer (Snag's form, an iframe from Snag on the page) talk by postMessage; the drawer and the
 * sign-in window too. The drawer says `ready`; the page sends `report` (what was picked, or nothing) and then `shot`
 * (the screenshot, once drawn). The drawer says `close`, `pick` (pick again), `shortcut` (Alt+Shift+S pressed inside
 * it), `signed-in` (the page may then show the phone button) and `filed`. The sign-in window sends the drawer `token`.
 */
export const MSG = {
  ready: "snag:ready",
  report: "snag:report",
  shot: "snag:shot",
  close: "snag:close",
  pick: "snag:pick",
  shortcut: "snag:shortcut",
  signedIn: "snag:signed-in",
  filed: "snag:filed",
  token: "snag:token",
  // a sandboxed frame (data-relay) and the page around it (an iframe with data-snag-frame), README › Pages in a frame
  /** frame → page: what was picked in the frame (`context`, or null), then `relayShot` (its screenshot) */
  relay: "snag:relay",
  relayShot: "snag:relay-shot",
  /** frame → page: a pick is starting (put the drawer away), or was cancelled (bring it back if it was open) */
  relayHide: "snag:relay-hide",
  relayCancel: "snag:relay-cancel",
  /** page → frame: start the picker (the shortcut pressed outside the frame, the phone button, Pick again) */
  relayPick: "snag:relay-pick",
} as const;

export interface ReportMessage {
  type: typeof MSG.report;
  project: string;
  build: string;
  /** null: an issue with nothing attached */
  context: IssueContext | null;
}

/** How long a sign-in lasts in one site's drawer before the sign-in window opens again. */
export const SESSION_DAYS = 30;

export const oneLine = (s: string) => s.replace(/\s+/g, " ").trim();
export const clip = (s: string, n: number) => (s.length > n ? `${s.slice(0, n - 1)}…` : s);
